import React, { FC, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { TweenMax, Power2 } from 'gsap';
import styled from 'styled-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { setHasFirstInteractionHappened } from '../store/application/actions';
import { showScreensaver, showTimeoutWarning } from '../store/screensaver/actions';
import { SofaIcons } from '../typings/enums';
import { RangeSettings, ScreensaverStep } from 'sofa-reborn-react';
import { localeService } from '../services';
import { KompisText } from '@inter-ikea-kompis/react-components';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';

let headerElement: HTMLDivElement;
let pointerElement: HTMLDivElement;
let clickArcElement: HTMLDivElement;
let videoContainerElement: HTMLDivElement;
let videoElement: HTMLVideoElement;

const StyledScreensaverContainer = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	right: 0;
	left: 0;
	z-index: 1000;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	background-color: ${SkapaTheme.color.neutral.white};
	display: flex;
`;

const ScreensaverText = styled.div`
	display: flex;
	justify-content: center;
	position: absolute;
	width: 100%;
	top: -250px;
	z-index: 200;
`;

const VideoContainer = styled.div`
	position: absolute;
	display: flex;
	justify-content: center;
	width: 100%;
	height: 100vh;
`;

const StyledIcon = styled.div`
	width: 400px;
	position: absolute;
	top: 70px;
`;

const StyledPointer = styled.div`
	opacity: 0;
	transform: matrix(0.1, 0, 0, 0.1, 0, 0);
	width: 400px;
	height: 500px;
`;

const StyledArc = styled.div`
	height: 145px;
	width: 145px;
	border: 13px solid ${SkapaTheme.color.border.light};
	border-radius: 50%;
	position: absolute;
	bottom: 320px;
	left: 70px;
	border-bottom-color: transparent;
	opacity: 0;
`;

const emptyAnimate = (): void => {
	return;
};

const showVideo = (): void => {
	TweenMax.to(videoContainerElement, 0.6, {
		delay: 0,
		top: 0,
		ease: Power2.easeInOut,
	});
};

const showHeader = (): void => {
	TweenMax.to(headerElement, 0.6, {
		delay: 0,
		top: 250,
		ease: Power2.easeInOut,
	});
	TweenMax.to(videoContainerElement, 0.6, {
		delay: 0,
		scale: 0.6,
		top: 150,
		ease: Power2.easeInOut,
	});
};

const showPointer = (): void => {
	TweenMax.to(videoContainerElement, 0.5, {
		delay: 0,
		opacity: 0.2,
		top: 1200,
		ease: Power2.easeIn,
	});

	TweenMax.to(videoContainerElement, 0.3, {
		delay: 0.5,
		scale: 1,
		opacity: 1,
	});

	TweenMax.to(headerElement, 0.5, {
		delay: 0,
		top: -250,
		ease: Power2.easeIn,
	});

	TweenMax.to(pointerElement, 0.5, {
		delay: 0.5,
		scale: 1,
		opacity: 1,
		ease: Power2.easeInOut,
	});

	TweenMax.to(clickArcElement, 0.5, {
		delay: 1.5,
		opacity: 1,
		ease: Power2.easeInOut,
		repeat: 3,
	});

	TweenMax.to(clickArcElement, 0.5, {
		delay: 6,
		opacity: 0,
		ease: Power2.easeInOut,
	});

	TweenMax.to(pointerElement, 0.5, {
		delay: 4.0,
		scale: 0.1,
		opacity: 0,
		ease: Power2.easeIn,
	});
};

const screensaverSteps: ScreensaverStep[] = [
	{
		duration: 300,
		video: 'none',
		animate: emptyAnimate,
		next: {},
	},
	{
		duration: 0,
		video: 'play',
		animate: showVideo,
		next: {},
	},
	{
		duration: 3000,
		video: 'stop',
		animate: showHeader,
		next: {},
	},
	{
		duration: 4000,
		video: 'none',
		animate: showPointer,
		next: {},
	},
];

screensaverSteps.forEach((step, index) => {
	step['next'] =
		(screensaverSteps[index + 1] as ScreensaverStep) || (screensaverSteps[0] as ScreensaverStep);
});

let currentScreensaverState = {
	step: screensaverSteps[0],
	next: screensaverSteps[1],
};
let transitionTimer: number;

const moveToNextAnimationStep = (): void => {
	const currentStep = currentScreensaverState.step;
	const duration = currentStep.duration;

	const isPlaying =
		videoElement.currentTime > 0 &&
		!videoElement.paused &&
		!videoElement.ended &&
		videoElement.readyState > 2;

	if (currentStep.video === 'play' && !isPlaying) {
		videoElement.load();
		videoElement.play();
		videoElement.onended = (): void => {
			if (currentStep.next && currentStep.next.next) {
				setTimeout(currentStep.next.animate as TimerHandler, 300);

				currentScreensaverState = {
					step: currentStep.next,
					next: currentStep.next.next,
				};

				moveToNextAnimationStep();
			}
		};
		return;
	} else if (currentStep.video === 'stop') {
		videoElement.pause();
	}

	transitionTimer = window.setTimeout(() => {
		if (currentStep.next && currentStep.next.next && currentStep.next.animate) {
			setTimeout(currentStep.next.animate, 300);

			currentScreensaverState = {
				step: currentStep.next,
				next: currentStep.next.next,
			};

			moveToNextAnimationStep();
		}
	}, duration);
};

const ScreensaverContainer: FC = () => {
	const rangeSettings = useSelector<RootState, RangeSettings>(
		(state) => state.settings.rangeSettings as RangeSettings
	);
	const isScreensaverShown = useSelector<RootState, boolean>(
		(state) => state.screensaver.showScreensaver
	);

	const dispatch = useDispatch<AppDispatch>();
	const onScreensaverClick = (): void => {
		if (isScreensaverShown) {
			dispatch(showScreensaver(false));
			dispatch(showTimeoutWarning({ show: false, sendEvent: false }));
			dispatch(setHasFirstInteractionHappened(false));
		}
	};

	useEffect(() => {
		if (isScreensaverShown) {
			moveToNextAnimationStep();
		}
		return (): void => {
			clearTimeout(transitionTimer);
		};
	}, [isScreensaverShown]);

	return (
		<StyledScreensaverContainer onClick={(): void => onScreensaverClick()}>
			<ScreensaverText ref={(div): HTMLDivElement => (headerElement = div as HTMLDivElement)}>
				<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.headingLarge}>
					{localeService.getTranslation('screensaverText')}
				</KompisText>
			</ScreensaverText>
			<VideoContainer
				ref={(div): HTMLDivElement => (videoContainerElement = div as HTMLDivElement)}
			>
				<video
					id="video"
					muted
					ref={(video): HTMLVideoElement => (videoElement = video as HTMLVideoElement)}
				>
					<source src={rangeSettings.screensaverSrc} type="video/mp4" />
				</video>
			</VideoContainer>
			<StyledPointer ref={(div): HTMLDivElement => (pointerElement = div as HTMLDivElement)}>
				<StyledArc ref={(div): HTMLDivElement => (clickArcElement = div as HTMLDivElement)} />
				<StyledIcon dangerouslySetInnerHTML={{ __html: SofaIcons.HandPointIcon }} />
			</StyledPointer>
		</StyledScreensaverContainer>
	);
};
export default ScreensaverContainer;
